<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const year = new Date().getFullYear()

const footerRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | undefined

/* Finale: die Riesen-Typo steigt scrub-gebunden aus dem Seitenende auf —
   endet exakt am maximalen Scroll, transform/opacity only. */
onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  gsap.registerPlugin(ScrollTrigger)

  ctx = gsap.context((self) => {
    const giant = self.selector!('.site-footer-giant')[0] as HTMLElement | undefined
    if (!giant) return
    gsap.from(giant, {
      yPercent: 36,
      autoAlpha: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: giant,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 1
      }
    })
  }, footerRef.value!)
})

onBeforeUnmount(() => ctx?.revert())

const productLinks = [
  { label: 'How it works', target: '#funktionsweise' },
  { label: 'Modules', target: '#module' },
  { label: 'Integrations', target: '#integrationen' },
  { label: 'Data Connectors', target: '#connectors' },
  { label: 'Plans', target: '#plans' }
]

/* Interne Seiten per `to`, externe/mailto per `href` */
const legalLinks: { label: string, to?: string, href?: string }[] = [
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Imprint', to: '/imprint' },
  { label: 'Report content', href: REPORT_MAILTO }
]

/* Kompakte Icon-Buttons in der Meta-Zeile — Docs, Status, Problem melden */
const quickLinks = [
  { icon: 'i-lucide-book-open', label: 'Docs', href: 'https://docs.clarify.bot' },
  { icon: 'i-lucide-activity', label: 'Status', href: 'https://status.clarify.bot' },
  { icon: 'i-lucide-bug', label: 'Report a problem', href: `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Clarify: problem report')}` }
]

const { scrollToTarget } = useSmoothScroll()
const route = useRoute()

/* Die Anker liegen auf der Startseite — von Unterseiten (Impressum) dorthin */
function go(target: string) {
  if (route.path !== '/') return navigateTo(`/${target}`)
  scrollToTarget(target)
}
</script>

<template>
  <footer ref="footerRef" class="site-footer">
    <div class="section-shell shell-gutter">
      <div class="site-footer-grid">
        <div class="site-footer-brand">
          <div class="flex items-center gap-3">
            <img src="/brand/clarify-mark.svg" alt="Clarify" width="40" height="40">
            <div>
              <div class="font-semibold text-lg" style="color: var(--nb-ink)">Clarify</div>
              <div class="text-sm" style="color: var(--nb-ink-faint)">The ticket bot for Discord</div>
            </div>
          </div>
          <p class="site-footer-claim copy-muted">
            Support on Discord: as organized as a helpdesk, as personal as a DM. Tickets in DMs, threads or channels, relayed to your team’s forum.
          </p>
        </div>

        <nav class="site-footer-col" aria-label="Product">
          <div class="site-footer-heading">Product</div>
          <!-- Echte Anker (crawlbar); Klick fährt weich per ScrollTo -->
          <a
            v-for="l in productLinks"
            :key="l.target"
            :href="`/${l.target}`"
            class="nav-link site-footer-link"
            @click.prevent="go(l.target)"
          >
            {{ l.label }}
          </a>
        </nav>

        <div class="site-footer-col">
          <div class="site-footer-heading">Legal</div>
          <template v-for="l in legalLinks" :key="l.label">
            <NuxtLink v-if="l.to" :to="l.to" class="nav-link site-footer-link">
              {{ l.label }}
            </NuxtLink>
            <a
              v-else
              :href="l.href"
              :target="l.href?.startsWith('mailto:') ? undefined : '_blank'"
              :rel="l.href?.startsWith('mailto:') ? undefined : 'noopener noreferrer'"
              class="nav-link site-footer-link"
            >
              {{ l.label }}
            </a>
          </template>
        </div>

        <div class="site-footer-col">
          <div class="site-footer-heading">Contact</div>
          <a :href="`mailto:${CONTACT_EMAIL}`" class="nav-link site-footer-link">Email support</a>
          <a href="https://docs.clarify.bot" target="_blank" rel="noopener noreferrer" class="nav-link site-footer-link">Documentation</a>
        </div>
      </div>

      <div class="site-footer-meta">
        <div class="site-footer-quick">
          <a
            v-for="l in quickLinks"
            :key="l.label"
            :href="l.href"
            :target="l.href.startsWith('mailto:') ? undefined : '_blank'"
            :rel="l.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'"
            class="site-footer-quick-btn"
            :aria-label="l.label"
            :title="l.label"
          >
            <UIcon :name="l.icon" class="size-[15px]" />
          </a>
        </div>

        <div style="color: var(--nb-ink-faint)">© {{ year }} Clarify. All rights reserved.</div>
      </div>
    </div>

    <!-- Nordbyte-Motiv: riesige Footer-Typografie -->
    <div class="site-footer-giant" aria-hidden="true">CLARIFY</div>
  </footer>
</template>

<style scoped>
.site-footer {
  position: relative;
  margin-top: 140px;
  padding-top: 72px;
  border-top: 1px solid var(--nb-hairline);
  overflow: hidden;
  /* Solide Fläche: die fixen page-rails enden visuell am Footer */
  background: var(--nb-bg);
}

.site-footer-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  padding-bottom: 48px;
}

.site-footer-brand {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 320px;
}

.site-footer-claim {
  font-size: 0.95rem;
}

.site-footer-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.site-footer-heading {
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
  margin-bottom: 4px;
}

.site-footer-link {
  font-size: 0.95rem;
  cursor: pointer;
  padding-bottom: 1px;
}

.site-footer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0 32px;
  border-top: 1px solid var(--nb-hairline);
  font-size: 0.85rem;
}

/* Icon-Buttons: Docs / Status / Problem melden — bewusst leise */
.site-footer-quick {
  display: flex;
  align-items: center;
  gap: 6px;
}

.site-footer-quick-btn {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-pill);
  border: 1px solid transparent;
  color: var(--nb-ink-faint);
  transition:
    color var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);
}

.site-footer-quick-btn:hover {
  color: var(--nb-ink);
  border-color: var(--nb-hairline-strong);
  background: var(--nb-accent-tint);
}

/* Nordbyte-Motiv: 14.7vw-Typo am Seitenende, dezent ausmaskiert */
.site-footer-giant {
  font-size: 14.7vw;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 0.78;
  text-align: center;
  color: transparent;
  background: linear-gradient(180deg, var(--nb-hairline-strong), transparent 88%);
  -webkit-background-clip: text;
  background-clip: text;
  user-select: none;
  pointer-events: none;
  transform: translateY(8%);
}

@media (min-width: 780px) {
  .site-footer-grid {
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 24px;
  }
}
</style>
